import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { addURLParam } from '../../util/addURLParam.js';
import { getAriaAttributes } from '../../util/getAriaAttributes/getAriaAttributes.js';
import { getDataAttributes } from '../../util/getDataAttributes/getDataAttributes.js';
import { ResizeObserverPolyfill } from '../../util/polyfills.js';
import { extractMarginsAndPaddings, getSpacing } from '../../util/spacing.js';
import { selectTokens } from '../../util/tokenSelector.js';
import { width, toStyled } from '@drone-js/media-query';
import { Box } from '../Box/Box.js';
import { useDirectionContext } from '../../contexts/session/Locale.js';
import { useThemeContext } from '../../contexts/session/Theme.js';
import { useEnvironmentContext } from '../../contexts/system/Environment.js';
import { extractFlexItemProps } from '../../types/Props.js';
import { ImageStyled } from './Image.styled.js';
import { ImagePreview as ImagePreviewMemoized } from './ImagePreview.js';

var _excluded = ["width", "height", "minWidth", "minHeight", "maxWidth", "maxHeight", "aspectRatio", "objectFit", "borderRadius"],
  _excluded2 = ["loading", "responsive", "width"],
  _excluded3 = ["alt", "src", "responsive", "requestWidth", "qM", "qL", "qXl", "aspectRatio", "blurHash", "objectFit", "srcSet", "onLoad", "disabled"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var mapSrc = function mapSrc(href, width) {
  return addURLParam(href, 's', "".concat(width, "x"));
};
var propTransform = function propTransform(props, theme, direction, withLayoutProps) {
  var width = props.width,
    height = props.height,
    minWidth = props.minWidth,
    minHeight = props.minHeight,
    maxWidth = props.maxWidth,
    maxHeight = props.maxHeight,
    aspectRatio = props.aspectRatio,
    objectFit = props.objectFit,
    borderRadius = props.borderRadius,
    remainingProps = _objectWithoutProperties(props, _excluded);
  var _extractMarginsAndPad = extractMarginsAndPaddings(remainingProps),
    margins = _extractMarginsAndPad.margins,
    paddings = _extractMarginsAndPad.paddings,
    restWithoutMargins = _extractMarginsAndPad.rest;
  var _extractFlexItemProps = extractFlexItemProps(restWithoutMargins),
    flexItemProps = _extractFlexItemProps.flexItemProps,
    rest = _extractFlexItemProps.rest;
  var spacing = getSpacing(_objectSpread(_objectSpread({}, margins), paddings), direction, theme.spacing);
  var token = selectTokens(theme.radius, borderRadius, 4);
  return {
    $css: _objectSpread(_objectSpread({
      width: width,
      height: height,
      minWidth: minWidth,
      minHeight: minHeight,
      maxWidth: maxWidth,
      maxHeight: maxHeight,
      aspectRatio: aspectRatio,
      objectFit: objectFit,
      borderRadius: withLayoutProps ? token : undefined
    }, flexItemProps), withLayoutProps && spacing),
    rest: rest
  };
};
/** Responsive image component, handles `srcSet` and `sizes` and tailored to be used with or without our CDN/pix servers. */
var ImageBase = function ImageBase(_ref) {
  var _ref$loading = _ref.loading,
    loading = _ref$loading === void 0 ? 'lazy' : _ref$loading,
    _ref$responsive = _ref.responsive,
    responsive = _ref$responsive === void 0 ? 'auto' : _ref$responsive,
    _ref$width = _ref.width,
    width$1 = _ref$width === void 0 ? '100%' : _ref$width,
    restProps = _objectWithoutProperties(_ref, _excluded2);
  var propsWithDefault = _objectSpread({
    loading: loading,
    responsive: responsive,
    width: width$1
  }, restProps);
  var props = _objectSpread({}, propsWithDefault);
  var environment = useEnvironmentContext();
  var theme = useThemeContext();
  var direction = useDirectionContext();
  var alt = props.alt,
    src = props.src,
    imageRequestType = props.responsive,
    requestWidth = props.requestWidth,
    qM = props.qM,
    qL = props.qL,
    qXl = props.qXl,
    aspectRatio = props.aspectRatio,
    blurHash = props.blurHash,
    objectFit = props.objectFit,
    srcSet = props.srcSet,
    onLoad = props.onLoad,
    disabled = props.disabled,
    remainingProps = _objectWithoutProperties(props, _excluded3);
  var _getDataAttributes = getDataAttributes(remainingProps),
    dataAttributes = _getDataAttributes.dataAttributes,
    remainingPropsWithoutData = _getDataAttributes.rest;
  var _getAriaAttributes = getAriaAttributes(remainingPropsWithoutData),
    ariaAttributes = _getAriaAttributes.ariaAttributes,
    remainingPropsWithoutDataAndAria = _getAriaAttributes.rest;
  var currentImageWidth = React.useRef(0);
  var imageRef = React.useRef();
  var imageWidths = Object.values(environment.image.width);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    loaded = _React$useState2[0],
    setLoaded = _React$useState2[1];

  // Calculate sizes once on load and after if element has been resized
  var createRef = React.useCallback(function (image) {
    if (!image) return null;
    var calculateSizes = function calculateSizes() {
      var _imageWidths$filter$;
      var offsetWidth = image.offsetWidth;
      var imageWidth = (_imageWidths$filter$ = imageWidths.filter(function (iw, i) {
        var _ref2;
        return offsetWidth <= (devicePixelRatio > 1 ?
        // Use geometric mean if DPR > 1.0
        Math.sqrt((_ref2 = iw * imageWidths[i - 1]) !== null && _ref2 !== void 0 ? _ref2 : Infinity) / devicePixelRatio : iw);
      })[0]) !== null && _imageWidths$filter$ !== void 0 ? _imageWidths$filter$ : Infinity;

      // Only update if we actually change breakpoint to avoid re-requesting same image
      if (imageWidth <= currentImageWidth.current) return;
      currentImageWidth.current = imageWidth;
      image.setAttribute('sizes', "".concat(offsetWidth, "px"));
    };
    imageRef.current = image;
    if (imageRequestType === 'auto') {
      try {
        var resizeObserver = new ResizeObserverPolyfill(calculateSizes);
        resizeObserver.observe(image);
        return function () {
          return resizeObserver.disconnect();
        };
      } catch (_unused) {
        calculateSizes();
      }
    }
    return null;
  }, [imageRequestType, imageWidths]);
  var getSizesFromProps = React.useCallback(function () {
    var sizeQueries = [{
      query: 'xl',
      data: qXl
    }, {
      query: 'l',
      data: qL
    }, {
      query: 'm',
      data: qM
    }];
    var sizes = sizeQueries.map(function (_ref3) {
      var query = _ref3.query,
        data = _ref3.data;
      return (data === null || data === void 0 ? void 0 : data.requestWidth) && "".concat(width(query, false).get(), " ").concat(data.requestWidth, "px");
    }).concat(requestWidth ? ["".concat(requestWidth, "px")] : []).filter(Boolean); // Remove any undefined or falsy values

    return sizes.join(', ');
  }, [requestWidth, qM, qL, qXl]);
  var handleImageLoad = React.useCallback(function (event) {
    if (loading === 'lazy-blur' && imageRef.current) {
      imageRef.current.classList.remove('drone-blur');
      return;
    }
    if (blurHash) {
      setLoaded(true);
    }
    onLoad === null || onLoad === void 0 || onLoad(event);
  }, [blurHash, loading, onLoad]);

  // Do not render image if no source is passed
  if (!src) return null;
  var dataKey = 'data:';
  var isData = src.substr(0, dataKey.length) === dataKey;
  var defaultSrcSet = isData || imageRequestType === 'original' ? undefined : imageWidths.map(function (imageWidth) {
    return "".concat(mapSrc(src, imageWidth), " ").concat(imageWidth, "w");
  }).join(', ');
  var srcBlur = loading === 'lazy-blur' ? mapSrc(src, environment.image.width.x) : undefined;
  var _propTransform = propTransform(_objectSpread({
      aspectRatio: aspectRatio,
      objectFit: objectFit
    }, remainingPropsWithoutDataAndAria), theme, direction, !blurHash),
    transformedProps = _propTransform.$css,
    rest = _propTransform.rest;
  var styledQuery = toStyled({
    qM: qM,
    qL: qL,
    qXl: qXl
  }, function (resultProps) {
    return propTransform(resultProps, theme, direction, !blurHash).$css;
  });
  var disabledProps = disabled && {
    opacity: 0.5,
    pointerEvents: 'none',
    filter: 'grayscale(100%)'
  };
  var imageElement = /*#__PURE__*/React.createElement(ImageStyled, _extends({}, !blurHash && _objectSpread(_objectSpread(_objectSpread({}, dataAttributes), ariaAttributes), rest), {
    ref: createRef,
    alt: alt,
    sizes: imageRequestType === 'manual' ? getSizesFromProps() : undefined,
    srcSet: srcSet || defaultSrcSet,
    src: src,
    loading: loading === 'immediate' ? 'eager' : 'lazy',
    $css: _objectSpread(_objectSpread({}, transformedProps), disabledProps),
    $srcBlur: srcBlur,
    className: srcBlur ? 'drone-blur' : undefined,
    onLoad: handleImageLoad,
    $q: styledQuery
  }, disabled && {
    'aria-hidden': true
  }));
  return blurHash ? /*#__PURE__*/React.createElement(Box, _extends({
    position: "relative",
    aspectRatio: aspectRatio,
    qM: qM,
    qL: qL,
    qXl: qXl,
    overflow: "hidden"
  }, rest, dataAttributes, ariaAttributes), /*#__PURE__*/React.createElement(ImagePreviewMemoized, {
    width: "100%",
    height: "100%",
    hash: blurHash,
    animateOut: loaded,
    "aria-label": alt
  }), imageElement) : imageElement;
};
ImageBase.Decorative = function (props) {
  return ImageBase(_objectSpread(_objectSpread({}, props), {}, {
    alt: ''
  }));
};
var ImageMemoized = /*#__PURE__*/React.memo(ImageBase);
ImageMemoized.displayName = 'Image';
ImageMemoized.Decorative = /*#__PURE__*/React.memo(ImageBase.Decorative);
ImageMemoized.Decorative.displayName = 'DecorativeImage';

export { ImageMemoized as Image, ImageBase };
//# sourceMappingURL=Image.js.map
